import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { AlertTriangle, XCircle } from 'lucide-react';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

interface CancelRegistrationModalProps {
  isOpen: boolean;
  eventName: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  isCancelling?: boolean;
  error?: string | null;
}

export const CancelRegistrationModal: React.FC<CancelRegistrationModalProps> = ({
  isOpen,
  eventName,
  onConfirm,
  onCancel,
  isCancelling = false,
  error = null,
}) => {
  const { t } = useTranslation('common');
  const [reason, setReason] = useState('');
  useBodyScrollLock(isOpen);

  // Reset reason when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) return;
    onConfirm(trimmedReason);
  };

  const isSubmitDisabled = isCancelling || !reason.trim();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <CardHeader className="pb-3 flex-shrink-0">
          <CardTitle className="flex items-center gap-2 text-lg text-[var(--color-neutral-900)]">
            <div className="bg-red-100 p-1.5 rounded-full">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            {t('upcomingEvents.cancelModal.title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4 overflow-y-auto pt-0">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800 mb-1">{t('upcomingEvents.cancelModal.warningTitle')}</p>
              <p className="text-sm text-red-700">
                {t('upcomingEvents.cancelModal.warningMessage')} <strong>{eventName}</strong>?
              </p>
              <p className="text-xs text-red-600 mt-2">{t('upcomingEvents.cancelModal.warningNote')}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancel-reason" className="text-sm font-medium text-[var(--color-neutral-700)]">
              {t('upcomingEvents.cancelModal.reasonLabel')} <span className="text-red-500">*</span>
            </Label>
            <textarea
              id="cancel-reason"
              placeholder={t('upcomingEvents.cancelModal.reasonPlaceholder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isCancelling}
              maxLength={500}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
            <p className="text-xs text-gray-500 text-right">
              {t('upcomingEvents.cancelModal.characters', { current: reason.length, max: 500 })}
            </p>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="outline" onClick={onCancel} disabled={isCancelling} className="flex-1">
              {t('upcomingEvents.cancelModal.keepRegistration')}
            </Button>
            <Button variant="destructive" onClick={handleConfirm} disabled={isSubmitDisabled} className="flex-1">
              {isCancelling ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2 flex-shrink-0" />
                  <span>{t('upcomingEvents.cancelModal.cancelling')}</span>
                </>
              ) : (
                <span>{t('upcomingEvents.cancelModal.confirmCancel')}</span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
